import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getCheckoutStatus, getConfig } from "@/lib/api";
import { PolicyFooter } from "@/components/PolicyFooter";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [productCode, setProductCode] = useState("BASIC");
  const [calendlyUrl, setCalendlyUrl] = useState("");

  useEffect(() => {
    getConfig().then((c) => setCalendlyUrl(c?.calendly_url || "")).catch(() => {});
  }, []);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const data = await getCheckoutStatus(sessionId);
        if (data.product_code) setProductCode(data.product_code);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (data.status === "expired") {
          setState("error");
          return;
        }
      } catch (e) {
        // keep trying
      }
      attempts += 1;
      if (attempts >= 6) {
        setState("pending");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
  }, [sessionId]);

  const hasSurvey = productCode === "BASIC" || productCode === "BUNDLE";
  const hasBooking = productCode === "STRATEGY" || productCode === "BUNDLE";

  return (
    <div className="min-h-screen bg-brand-bone flex items-center justify-center px-4">
      <div className="max-w-md w-full rounded-2xl border border-border bg-white p-8 md:p-10 shadow-sm text-center" data-testid="checkout-success-card">
        {state === "checking" && (
          <>
            <Loader2 className="w-12 h-12 text-brand-green animate-spin mx-auto" />
            <h1 className="font-heading text-2xl font-bold mt-5">Confirming your payment…</h1>
            <p className="text-brand-ink/60 mt-2">This only takes a moment.</p>
          </>
        )}

        {state === "paid" && (
          <>
            <div className="w-16 h-16 rounded-full bg-brand-green/10 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-9 h-9 text-brand-green" />
            </div>
            <h1 className="font-heading text-2xl font-bold mt-5">Payment received!</h1>
            <p className="text-brand-ink/70 mt-2">
              {productCode === "BUNDLE"
                ? "Your bundle is unlocked — take your survey and book your strategy call below. A confirmation email is on its way."
                : hasBooking
                ? "Your calendar access is unlocked. Book your 30-minute strategy call with Frank below."
                : "Your survey is unlocked and a confirmation email is on its way. Let's find those leaks."}
            </p>
            {hasBooking && (
              <p className="text-xs text-brand-ink/50 mt-3">
                Calls available Mon–Fri, 10:00 AM – 2:00 PM ET · last start 1:30 PM ET.
              </p>
            )}

            <div className="mt-7 flex flex-col gap-3">
              {hasSurvey && (
                <Button
                  data-testid="success-start-survey"
                  onClick={() => navigate("/survey", { state: { access_type: "paid", access_ref: sessionId } })}
                  className="rounded-full bg-brand-gold hover:bg-brand-goldDark text-white px-8 py-6"
                >
                  Start My Survey <ArrowRight className="w-5 h-5 ml-1" />
                </Button>
              )}
              {hasBooking && (
                <a
                  data-testid="success-book-call"
                  href={calendlyUrl || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-green hover:bg-brand-greenDark text-white px-8 py-4 font-medium transition-all hover:-translate-y-0.5"
                >
                  <Calendar className="w-5 h-5" /> Book My Strategy Call
                </a>
              )}
            </div>
          </>
        )}

        {state === "pending" && (
          <>
            <Loader2 className="w-12 h-12 text-brand-gold animate-spin mx-auto" />
            <h1 className="font-heading text-2xl font-bold mt-5">Still processing</h1>
            <p className="text-brand-ink/70 mt-2">
              Your payment is being processed. You'll receive a confirmation email shortly.
            </p>
            {hasSurvey ? (
              <Button
                onClick={() => navigate("/survey", { state: { access_type: "paid", access_ref: sessionId } })}
                className="mt-6 rounded-full bg-brand-green hover:bg-brand-greenDark text-white px-8"
                data-testid="pending-start-survey"
              >
                Try to open survey
              </Button>
            ) : (
              <Button onClick={() => navigate("/")} variant="outline" className="mt-6 rounded-full" data-testid="pending-home">
                Back to home
              </Button>
            )}
          </>
        )}

        {state === "error" && (
          <>
            <XCircle className="w-12 h-12 text-destructive mx-auto" />
            <h1 className="font-heading text-2xl font-bold mt-5">Something went wrong</h1>
            <p className="text-brand-ink/70 mt-2">We couldn't confirm this payment session.</p>
            <Button onClick={() => navigate("/")} variant="outline" className="mt-6 rounded-full" data-testid="error-home">
              Back to home
            </Button>
          </>
        )}
        <PolicyFooter className="mt-8" />
      </div>
    </div>
  );
}
